import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { QrCode, X, CameraSlash, Upload, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * QR-Scan-Button. Öffnet die Kamera als Live-Scanner. Erkennt einen Vehicle-QR-Code
 * (Format "VEH:<id>" oder reine ID/Kennzeichen) und navigiert auf /scan/<token>.
 *
 * Props:
 *  - className?: extra CSS class for the trigger button
 *  - label?: button label (default: 'Scannen')
 *  - iconOnly?: bool — show only icon (for header use)
 */
const QrScannerButton = ({ className = '', label = 'Scannen', iconOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [activeCameraId, setActiveCameraId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [manualToken, setManualToken] = useState('');
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const containerId = 'qr-scanner-region';
  const navigate = useNavigate();

  const stopScanner = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (!s) return;
    try {
      // Only call stop() if scanner is currently scanning (state === 2)
      if (s.getState && s.getState() === 2) {
        await s.stop();
      }
      await s.clear();
    } catch (_) {
      // ignore
    }
  };

  const startWithDeviceId = async (deviceId) => {
    setError(null);
    setStarting(true);
    try {
      // Make sure any previous instance is properly stopped before starting a new one
      await stopScanner();
      // Wait for the DOM element to actually be in the document (Dialog opens async)
      let el = document.getElementById(containerId);
      let attempts = 0;
      while (!el && attempts < 40) {
        await new Promise((r) => setTimeout(r, 50));
        el = document.getElementById(containerId);
        attempts += 1;
      }
      if (!el) {
        throw new Error('Scanner-Bereich konnte nicht initialisiert werden');
      }
      const scanner = new Html5Qrcode(containerId, {
        verbose: false,
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      });
      scannerRef.current = scanner;
      const cameraConfig = deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'environment' };
      // Responsive qrbox: ~70% of smaller video dimension (avoid hard 240x240)
      const qrboxFn = (vw, vh) => {
        const minEdge = Math.min(vw || 320, vh || 320);
        const size = Math.max(160, Math.floor(minEdge * 0.7));
        return { width: size, height: size };
      };
      await scanner.start(
        cameraConfig,
        { fps: 15, qrbox: qrboxFn, disableFlip: false },
        (decoded) => handleDecode(decoded),
        () => {}
      );
      setActiveCameraId(deviceId || null);
    } catch (err) {
      const name = err?.name || '';
      let msg = err?.message || 'Kamera konnte nicht gestartet werden';
      if (name === 'NotAllowedError' || /permission/i.test(msg)) {
        msg = 'Kamerazugriff verweigert. Bitte im Browser erlauben (Adressleiste → Berechtigungen) und Seite neu laden.';
      } else if (name === 'NotFoundError' || /no camera|requested device/i.test(msg)) {
        msg = 'Keine Kamera gefunden. Bitte ein Gerät mit Kamera (z.B. Smartphone) verwenden.';
      } else if (name === 'NotReadableError') {
        msg = 'Kamera ist bereits in Verwendung. Bitte andere Apps schließen und erneut versuchen.';
      } else if (/secure|https/i.test(msg)) {
        msg = 'Kamera benötigt eine sichere HTTPS-Verbindung.';
      }
      setError(msg);
      await stopScanner();
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (!open) {
      stopScanner();
      return;
    }
    let cancelled = false;
    const init = async () => {
      try {
        const list = await Html5Qrcode.getCameras();
        if (cancelled) return;
        setCameras(list || []);
        // Prefer rear camera (label contains "back" or "rear" or "environment")
        let preferred = (list || []).find((c) => /back|rear|environment|hinten|haupt/i.test(c.label));
        if (!preferred && list && list.length > 0) preferred = list[list.length - 1];
        await startWithDeviceId(preferred ? preferred.id : null);
      } catch (err) {
        if (cancelled) return;
        // getCameras() itself may fail (permission denied)
        const name = err?.name || '';
        let msg = err?.message || 'Kamera konnte nicht initialisiert werden';
        if (name === 'NotAllowedError' || /permission|denied/i.test(msg)) {
          msg = 'Kamerazugriff verweigert. Bitte im Browser erlauben und Seite neu laden.';
        }
        setError(msg);
      }
    };
    init();
    return () => {
      cancelled = true;
      stopScanner();
    };
  }, [open]);

  const handleDecode = (text) => {
    const cleaned = (text || '').trim();
    if (!cleaned) return;
    stopScanner().finally(() => proceed(cleaned));
  };

  const proceed = (token) => {
    setOpen(false);
    toast.success('QR-Code erkannt');
    // Falls der QR-Inhalt eine URL zur Scan-Seite ist (z.B. Handy-Kamera-Scan
    // öffnet die URL direkt), extrahiere das Token-Suffix.
    let finalToken = token;
    try {
      if (/^https?:\/\//i.test(token)) {
        const url = new URL(token);
        const m = url.pathname.match(/\/scan\/(.+)$/);
        if (m && m[1]) finalToken = decodeURIComponent(m[1]);
      }
    } catch (_) { /* keep raw token */ }
    navigate(`/scan/${encodeURIComponent(finalToken)}`);
  };

  const switchCamera = async () => {
    if (cameras.length < 2) return;
    const idx = cameras.findIndex((c) => c.id === activeCameraId);
    const next = cameras[(idx + 1) % cameras.length];
    await startWithDeviceId(next.id);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    await stopScanner();
    setError(null);
    setStarting(true);
    try {
      const tempScanner = new Html5Qrcode(containerId, {
        verbose: false,
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      });
      const result = await tempScanner.scanFile(file, false);
      await tempScanner.clear();
      handleDecode(result);
    } catch (err) {
      setError('Im Bild wurde kein QR-Code erkannt. Bitte schärferes Foto verwenden.');
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size={iconOnly ? 'icon' : 'sm'}
        className={`gap-1.5 ${className}`}
        onClick={() => { setError(null); setOpen(true); }}
        data-testid="qr-scan-trigger"
        aria-label="QR-Code scannen"
      >
        <QrCode size={16} weight="bold" />
        {!iconOnly && <span>{label}</span>}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="qr-scan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode size={18} weight="bold" className="text-[#E60000]" />
              Fahrzeug-QR scannen
            </DialogTitle>
          </DialogHeader>

          {/* Scanner DOM target is always rendered so html5-qrcode can attach.
              When error or starting, we overlay a friendly message on top. */}
          <div className="relative w-full bg-black rounded-sm overflow-hidden" style={{ aspectRatio: '1 / 1' }}>
            <div id={containerId} className="w-full h-full" />
            {starting && !error && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm bg-black/70">
                Kamera wird gestartet…
              </div>
            )}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center p-4 bg-[#1A0000]/95 text-center">
                <div className="text-[#FFD0D0]">
                  <CameraSlash size={32} className="mx-auto mb-2" />
                  <p className="font-semibold text-sm mb-1">Kamera nicht verfügbar</p>
                  <p className="text-xs">{error}</p>
                </div>
              </div>
            )}
          </div>

          {!error && (
            <p className="text-xs text-[#525866] text-center -mt-1">
              Halte den QR-Code im Sichtfeld – er wird automatisch erkannt.
            </p>
          )}

          {/* Manuelle Kennzeichen-Eingabe als Fallback */}
          <div className="border-t border-[#E2E4E9] pt-3 mt-1">
            <p className="text-xs text-[#8A94A6] mb-1.5">Funktioniert der Scanner nicht? Kennzeichen eingeben:</p>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const t = manualToken.trim();
                if (t) proceed(t.toUpperCase());
              }}
            >
              <input
                type="text"
                value={manualToken}
                onChange={(e) => setManualToken(e.target.value)}
                placeholder="z.B. KN-CI188"
                className="flex-1 px-3 py-2 border border-[#E2E4E9] rounded-sm text-sm focus:border-[#E60000] focus:outline-none"
                data-testid="qr-manual-input"
              />
              <Button
                type="submit"
                size="sm"
                className="btn-primary"
                disabled={!manualToken.trim()}
                data-testid="qr-manual-submit"
              >
                Öffnen
              </Button>
            </form>
          </div>

          <div className="flex flex-wrap gap-2 justify-between">
            <div className="flex gap-2 flex-wrap">
              {cameras.length > 1 && !error && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={switchCamera}
                  disabled={starting}
                  data-testid="qr-switch-camera"
                >
                  <ArrowsClockwise size={14} className="mr-1" /> Kamera wechseln
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                data-testid="qr-file-upload"
              >
                <Upload size={14} className="mr-1" /> Foto hochladen
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
              <X size={14} className="mr-1" /> Abbrechen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QrScannerButton;
